<template>
  <Menubar
    v-bind="$attrs"
    :model="model"
    :pt="{
      root: rootClass,
      menu: 'flex items-center gap-1',
      submenu:
        'rounded-xl border [border-color:var(--stw-glass-border)] bg-surface-50',
      button: 'xl:hidden hover:text-surface-950',
      // Top-level items pick up the right (dark) color via plain
      // inheritance, but PrimeVue's own Menubar CSS sets an explicit
      // (light, dark-theme-tuned) color on submenu item links that
      // inheritance alone doesn't beat — force it explicitly instead of
      // relying on ambient color for both levels.
      itemContent:
        '!text-[var(--p-text-color)] rounded-lg px-2 py-1.5 text-sm hover:!text-[var(--p-text-hover-color)] hover:![background-color:var(--stw-glass-bg-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:[--tw-ring-color:var(--stw-glass-border-strong)]',
      itemLink: '!text-inherit',
      separator: '[background-color:var(--stw-glass-border)] mx-1 h-5 w-px',
      icon: 'text-surface-500',
      label: 'font-medium',
    }"
  >
    <template #start>
      <slot name="start" />
    </template>

    <template #end>
      <div class="ml-auto flex items-center gap-2">
        <slot name="end" />
      </div>
    </template>

    <!-- Optional custom item rendering, passed straight through. -->
    <template v-if="$slots.item" #item="slotProps">
      <slot name="item" v-bind="slotProps" />
    </template>
  </Menubar>
</template>

<script setup lang="ts">
import { Menubar } from 'primevue';
import { computed } from 'vue';

interface MenuItem {
  key?: string;
  label?: string;
  icon?: string;
  command?: () => void;
  items?: MenuItem[];
}

interface Props {
  model?: MenuItem[];
  elevated?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  model: () => [],
  elevated: false,
});

const rootClass = computed(() => {
  const base =
    'sticky top-0 z-50 w-full bg-surface-50 border-b [border-color:var(--stw-glass-border)]';
  const elevation = props.elevated ? 'shadow-md' : '';
  const sizing = 'h-14 px-4 xl:px-6';
  return [base, elevation, sizing].join(' ');
});
</script>

<style scoped>
/* Prevent text selection on menu items */
:deep(.p-menubar-item-content) {
  user-select: none;
}

/* Override PrimeVue's .p-focus styling to prevent stuck hover highlight */
:deep(.p-menubar-item.p-focus > .p-menubar-item-content) {
  background-color: transparent !important;
  color: inherit !important;
}

:deep(.p-menubar-item.p-focus > .p-menubar-item-content .p-menubar-item-icon) {
  color: var(--p-text-muted-color) !important;
}

/* PrimeVue's own precompiled menubar.css sets the open/active submenu
   trigger's background via `bg-surface-800 dark:bg-surface-100` — a
   Tailwind dark: pair assuming the *standard* (unflipped) surface scale.
   Our --p-surface-* values are intentionally flipped (light theme is the
   default with no `dark` class applied), so `bg-surface-800` there
   resolves to a dark tone instead of the near-white it was meant to be,
   with no `dark` class ever active to supply the intended override either.
   Force it explicitly rather than relying on that pairing. */
:deep(.p-menubar-item-active > .p-menubar-item-content) {
  background-color: var(--stw-glass-bg-strong) !important;
  color: var(--p-text-color) !important;
}
:deep(.p-menubar-item-active > .p-menubar-item-content .p-menubar-item-icon),
:deep(
  .p-menubar-item-active > .p-menubar-item-content .p-menubar-submenu-icon
) {
  color: var(--p-text-muted-color) !important;
}

:deep(
  .p-menubar-item.p-focus > .p-menubar-item-content .p-menubar-submenu-icon
) {
  color: var(--p-text-muted-color) !important;
}
</style>
